import * as React from 'react'

import { orderBy } from 'lodash'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import {
  PickerContainer,
  PickerOption,
} from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/StructuredPicker/shared'
import {
  CostCenterOption,
  useCostCenterByLegalEntities,
} from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'

interface Props {
  value?: { id: string; name: string }
  placeholder: string
  selectedIds: number[]
  legalEntityIds: number[]
  onChange: (option: CostCenterOption) => void
  readonly?: boolean
}

const WorkplacePicker = ({
  value,
  placeholder,
  selectedIds: _selectedIds,
  legalEntityIds,
  onChange,
  readonly,
}: Props) => {
  const [open, setOpen] = React.useState(false)

  const {
    workplaceOptions: options,
    controls,
    loading,
    debouncedOnSearch,
    clearSearch,
  } = useCostCenterByLegalEntities({
    excludeCostCenterIds: [],
    legalEntityIds,
    onlyWorkplace: true,
  })

  useUnsafeEffect(() => {
    if (!open) {
      clearSearch()
    }
  }, [open, clearSearch])

  const handleValueChange = (option: CostCenterOption) => {
    setOpen(false)
    onChange(option)
  }

  const sortedOptions = React.useMemo(() => {
    return orderBy(options, (o) => o.name, ['asc'])
  }, [options])

  const unselectedValue = !value || Number(value.id) < 0

  const isOptionSelected = (optionId: string) => {
    return value?.id === optionId
  }

  return (
    <PickerContainer
      open={open}
      readonly={readonly}
      onOpenChange={() => setOpen(!open)}
      disabled={options.length < 1 || !!readonly}
      placeholder={placeholder}
      displayValue={unselectedValue ? undefined : value?.name}
      onSearchChange={debouncedOnSearch}
      loading={loading}
      controls={controls}
      hasOptions={options.length > 0}
    >
      {sortedOptions.map((option) => (
        <PickerOption
          key={option.id}
          option={option}
          isSelected={isOptionSelected(option.id)}
          onSelect={handleValueChange}
        />
      ))}
    </PickerContainer>
  )
}

export default WorkplacePicker
